import React from "react";
import "./style.css";
import { Button, Form } from "react-bootstrap";
import { MarkdownTextArea } from "@components/FormsV2";
import { useState } from "react";
import { handleAlert } from "@utils/helperFunctions";
import useFlags from "@common/hooks/useFlags";
import { useGetProjectById } from "../ProjectServicesV2/Show/api";
import { useEffect, useRef } from "react";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import store from "../../store";
import {
  useUpdateProjectMutation,
  useCreateExecutiveSummaryRequestMutation,
  useGetAccountQuery,
  generated,
} from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagicWandSparkles } from "@fortawesome/pro-solid-svg-icons";
import ReactMarkdown from "react-markdown";

interface Fields {
  executiveSummary: string;
  solutionSummary: string;
  ourResponsibilities: string;
  customerResponsibilities: string;
  outOfScope: string;
}

const DEFAULT_LABELS: Record<keyof Fields, string> = {
  executiveSummary: "Executive Summary",
  solutionSummary: "Solution Summary",
  ourResponsibilities: "Our Responsibilities",
  customerResponsibilities: "Customer Responsibilities",
  outOfScope: "Out of Scope",
};

const LABEL_KEYS: Record<keyof Fields, string> = {
  executiveSummary: "executive_summary",
  solutionSummary: "solution_summary",
  ourResponsibilities: "our_responsibilities",
  customerResponsibilities: "client_responsibilities",
  outOfScope: "out_of_scope",
};

const CustomerSummaryV2 = (props) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateProject] = useUpdateProjectMutation();
  const [createSummaryRequest] = useCreateExecutiveSummaryRequestMutation();
  const id = props.project_id;
  const { project, isLoading } = useGetProjectById(id);
  const {
    configCustomerSummaryState,
    releaseExecutiveSummaryGenerator,
    releaseTiptapEditor,
  } = useFlags();
  const { hasPrivilege } = usePrivilegeCheck();
  const { data: account } = useGetAccountQuery(
    { slug: accountSlug },
    { skip: !accountSlug }
  );
  const fieldLabels = (account?.data?.attributes as any)?.["field-labels"];
  const labelFor = (key: keyof Fields) =>
    fieldLabels?.[LABEL_KEYS[key]] || DEFAULT_LABELS[key];
  const [fields, setFields] = useState<Fields>({
    executiveSummary: "",
    solutionSummary: "",
    ourResponsibilities: "",
    customerResponsibilities: "",
    outOfScope: "",
  });
  const [responseMessage, setResponseMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFail, setShowFail] = useState(false);
  const [showSummaryRequestAlert, setShowSummaryRequestAlert] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!isLoading && project) {
      setFields({
        executiveSummary: project.executiveSummary || "",
        solutionSummary: project.solutionSummary || "",
        ourResponsibilities: project.ourResponsibilities || "",
        customerResponsibilities: project.customerResponsibilities || "",
        outOfScope: project.outOfScope || "",
      });
    }
  }, [isLoading]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  //@ts-ignore
  const minimized = configCustomerSummaryState === "minimized";
  const keys = Object.keys(fields);

  const handleSave = async () => {
    const data = {
      id: Number(id),
      type: "projects",
      attributes: {
        "executive-summary": fields.executiveSummary,
        "solution-summary": fields.solutionSummary,
        "our-responsibilities": fields.ourResponsibilities,
        "client-responsibilities": fields.customerResponsibilities,
        "out-of-scope": fields.outOfScope,
      },
    };

    const res = await updateProject({
      slug: accountSlug,
      id: props.project_id,
      //@ts-ignore
      body: { data },
    });

    //@ts-ignore
    if (res?.data?.data?.id) {
      setResponseMessage("Customer summary updated");
      setShowSuccess(true);
      setIsDirty(false);
    } else {
      handleFailResponse("Something went wrong updating customer summary");
    }
  };

  const handleFailResponse = (msg) => {
    setResponseMessage(msg || "Something went wrong processing this request");
    setShowFail(true);
  };

  const handleGenerateSummary = async () => {
    setShowSummaryRequestAlert(true);
    setGenerating(true);

    const res = await createSummaryRequest({
      slug: accountSlug,
      projectId: id,
      body: {},
    });

    //@ts-ignore
    const pending = res?.data?.data;
    let status = pending?.attributes?.status || "pending";

    if (pending?.id) {
      if (status === "complete") {
        const updated = {
          ...fields,
          executiveSummary: pending?.attributes?.["executive-summary"] || "",
        };
        setFields(updated);
        setShowSummaryRequestAlert(false);
        setGenerating(false);
        setIsDirty(true);
        return;
      } else {
        while (true) {
          const latest = await store.dispatch(
            generated.endpoints.getExecutiveSummaryRequest.initiate({
              slug: accountSlug,
              projectId: id,
              id: pending?.id,
            })
          );
          const status = latest?.data?.data?.attributes?.status;

          if (status === "complete" || status === "failed") {
            const executiveSummary =
              latest?.data?.data?.attributes?.["executive-summary"] || "";
            setFields({ ...fields, executiveSummary });
            setShowSummaryRequestAlert(false);
            setGenerating(false);
            setIsDirty(true);
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      }
    } else {
      setGenerating(false);
      setShowSummaryRequestAlert(false);
      handleFailResponse("");
    }
  };

  const building = project?.status === "building";
  const disabled =
    !building ||
    !hasPrivilege("projects.overview", ["create", "manage", "purge"]);
  const renderV1Editor = (key, i) =>
    disabled ? (
      <div className={`mdtav2 mdtav2-disabled`}>
        <ReactMarkdown>{fields[key]}</ReactMarkdown>
      </div>
    ) : (
      <MarkdownTextArea
        key={key}
        value={fields[key]}
        placeholder={`Enter ${labelFor(key as keyof Fields).toLowerCase()}`}
        onChange={() => setIsDirty(true)}
        onBlur={(markdown) => setFields({ ...fields, [key]: markdown })}
        customToolbarItems={
          i === 0 && releaseExecutiveSummaryGenerator
            ? [
                {
                  tooltip: "Generate content based on project details",
                  className: "toastui-editor-toolbar-icons-writeit",
                  command: "generateSummary",
                  name: "generateSummary",
                  handler: handleGenerateSummary,
                },
              ]
            : []
        }
      />
    );

  return (
    <div className="customerSummaryV2">
      <div className="mainHeader">
        <div className="title">Solution Summary and Responsibilites</div>
        <div className="actionBtns">
          {building && (
            <Button
              className="btnSeafoam"
              onClick={handleSave}
              style={{ border: "2px solid #418172" }}
              disabled={disabled}
            >
              Save
            </Button>
          )}
        </div>
      </div>
      <div className="customerSummaryContent">
        {(minimized ? keys.slice(0, 2) : keys).map((key, i) => (
          <React.Fragment key={key}>
            <Form.Label>{labelFor(key as keyof Fields)}</Form.Label>
            {i === 0 && generating ? (
              <div style={{ minHeight: "147px" }}>
                <ScopeStackSpinner />
              </div>
            ) : releaseTiptapEditor ? (
              <MarkdownTextAreaV2
                key={key}
                value={fields[key]}
                placeholder={`Enter ${labelFor(
                  key as keyof Fields
                ).toLowerCase()}`}
                alwaysEdit={!disabled}
                disabled={disabled}
                onChange={(markdown) => {
                  setFields((prev) => ({ ...prev, [key]: markdown }));
                  setIsDirty(true);
                }}
                customToolbarButtons={
                  i === 0 && releaseExecutiveSummaryGenerator
                    ? [
                        {
                          name: "generateSummary",
                          icon: <FontAwesomeIcon icon={faMagicWandSparkles} />,
                          tooltip: "Generate content based on project details",
                          action: () => handleGenerateSummary(),
                        },
                      ]
                    : []
                }
              />
            ) : (
              renderV1Editor(key, i)
            )}
          </React.Fragment>
        ))}
      </div>
      <ScopeStackModal
        modalTitle="Generate Executive Summary"
        modalBody={
          <p>
            Analyzing your project and writing an executive summary. Depending
            on the complexity of your project this may take 30-60 seconds
          </p>
        }
        button1Text="Close"
        handleButton1Click={() => setShowSummaryRequestAlert(false)}
        button2Text={null}
        handleButton2Click={null}
        show={showSummaryRequestAlert}
        handleClose={() => setShowSummaryRequestAlert(false)}
      />
      {handleAlert(
        showSuccess,
        responseMessage,
        setShowSuccess,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFail,
        responseMessage,
        setShowFail,
        "warning",
        ToastAlert
      )}
    </div>
  );
};

export default CustomerSummaryV2;
